<template>
  <div class="module-mgmt-container">
    <!-- 模块状态统计 -->
    <div class="stats-row">
      <div class="stat-card">
        <span class="stat-value">{{ modules.length }}</span>
        <span class="stat-label">{{ $t('moduleMgmt.totalModules') }}</span>
      </div>
      <div class="stat-card enabled">
        <span class="stat-value">{{ enabledCount }}</span>
        <span class="stat-label">{{ $t('moduleMgmt.enabled') }}</span>
      </div>
      <div class="stat-card">
        <span class="stat-value">{{ disabledCount }}</span>
        <span class="stat-label">{{ $t('moduleMgmt.disabled') }}</span>
      </div>
      <div class="stat-card">
        <el-button type="text" @click="showDisabledModules = true">
          {{ $t('moduleMgmt.viewDisabledModules') }} <el-icon><ArrowRight /></el-icon>
        </el-button>
      </div>
    </div>

    <!-- 模块卡片列表 -->
    <div class="module-card">
      <div class="card-header">
        <div class="card-title-wrapper">
          <h3 class="card-title">{{ $t('moduleMgmt.moduleManagement') }}</h3>
          <span class="info-tag">{{ $t('moduleMgmt.enableDisableManagement') }}</span>
        </div>
        <div class="card-actions">
          <el-button type="info" size="small" @click="refreshModules">
            <el-icon><Refresh /></el-icon>
            {{ $t('moduleMgmt.refresh') }}
          </el-button>
        </div>
      </div>

      <div class="card-body">
        <div v-if="modules.length === 0" class="empty-state">
          <el-icon><InfoFilled /></el-icon>
          <p>{{ $t('moduleMgmt.noModuleInfo') }}</p>
        </div>

        <div v-else class="module-grid">
          <div v-for="mod in modules" :key="mod.name" class="module-item">
            <div class="module-header">
              <div class="module-icon" :class="mod.name">
                <el-icon><component :is="getModuleIcon(mod.name)" /></el-icon>
              </div>
              <div class="module-info">
                <h4 class="module-name">{{ getModuleDisplayName(mod.name) }}</h4>
                <span class="module-category">{{ getModuleCategory(mod.category) }}</span>
              </div>
              <div class="module-status">
                <el-tag v-if="mod.enabled" type="success" size="small" effect="dark">{{ $t('moduleMgmt.enabled') }}</el-tag>
                <el-tag v-else type="info" size="small">{{ $t('moduleMgmt.disabled') }}</el-tag>
              </div>
            </div>

            <div class="module-body">
              <div class="status-row">
                <span class="label">{{ $t('moduleMgmt.status') }}:</span>
                <span class="value" :class="getStatusClass(mod)">
                  {{ getStatusText(mod) }}
                </span>
              </div>
              <div v-if="mod.error" class="status-row error">
                <span class="label">{{ $t('moduleMgmt.error') }}:</span>
                <span class="value error-text">{{ mod.error }}</span>
              </div>
            </div>

            <!-- 依赖信息 -->
            <div v-if="getModuleDeps(mod.name).length > 0" class="deps-info">
              <span class="deps-label">{{ $t('moduleMgmt.dependency') }}:</span>
              <el-tag
                v-for="dep in getModuleDeps(mod.name)"
                :key="dep"
                size="small"
                type="info"
                class="dep-tag"
              >
                {{ getModuleDisplayName(dep) }}
              </el-tag>
            </div>

            <div class="module-actions">
              <!-- 启用/禁用按钮 -->
              <!-- 启用/禁用按钮始终显示 -->
              <el-button
                type="primary"
                size="small"
                @click="toggleModule(mod)"
              >
                {{ mod.enabled ? $t('moduleMgmt.disable') : $t('moduleMgmt.enable') }}
              </el-button>
              <el-button
                type="info"
                size="small"
                @click="showModuleInfo(mod.name)"
                icon="Info"
              >
                {{ $t('moduleMgmt.details') }}
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 模块详情对话框 -->
    <el-dialog
      v-model="infoDialogVisible"
      :title="$t('moduleMgmt.moduleDetails') + ' - ' + (currentModuleInfo.displayName || currentModuleInfo.name)"
      width="500px"
    >
      <el-descriptions :column="1" border v-if="currentModuleInfo.name">
        <el-descriptions-item :label="$t('moduleMgmt.name')">{{ currentModuleInfo.displayName || currentModuleInfo.name }}</el-descriptions-item>
        <el-descriptions-item :label="$t('moduleMgmt.category')">{{ currentModuleInfo.category || '-' }}</el-descriptions-item>
        <el-descriptions-item :label="$t('moduleMgmt.description')">{{ currentModuleInfo.description || '-' }}</el-descriptions-item>
        <el-descriptions-item :label="$t('moduleMgmt.version')">{{ currentModuleInfo.version || '-' }}</el-descriptions-item>
        <el-descriptions-item :label="$t('moduleMgmt.status')">
          <el-tag v-if="currentModuleInfo.running" type="success">{{ $t('moduleMgmt.running') }}</el-tag>
          <el-tag v-else-if="currentModuleInfo.enabled" type="warning">{{ $t('moduleMgmt.notEnabled') }}</el-tag>
          <el-tag v-else type="info">{{ $t('moduleMgmt.disabled') }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('moduleMgmt.dependency')">
          <span v-if="!currentModuleInfo.depends || currentModuleInfo.depends.length === 0">{{ $t('moduleMgmt.noDependency') }}</span>
          <el-tag v-else v-for="dep in currentModuleInfo.depends" :key="dep" size="small" class="dep-tag">
            {{ getModuleDisplayName(dep) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('moduleMgmt.dependedBy')">
          <span v-if="!currentModuleInfo.dependents || currentModuleInfo.dependents.length === 0">{{ $t('moduleMgmt.noDependency') }}</span>
          <el-tag v-else v-for="dep in currentModuleInfo.dependents" :key="dep" size="small" type="warning" class="dep-tag">
            {{ getModuleDisplayName(dep) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('moduleMgmt.dependencySatisfied')">
          <el-tag :type="currentModuleInfo.depsSatisfied ? 'success' : 'danger'">
            {{ currentModuleInfo.depsSatisfied ? $t('moduleMgmt.yes') : $t('moduleMgmt.no') }}
          </el-tag>
        </el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button @click="infoDialogVisible = false">{{ $t('moduleMgmt.close') }}</el-button>
        <el-button
          type="primary"
          @click="fetchModuleInfo(currentModuleInfo.name)"
          :loading="infoLoading"
        >
          {{ $t('moduleMgmt.refresh') }}
        </el-button>
      </template>
    </el-dialog>

    <!-- 禁用模块对话框 -->
    <el-dialog
      v-model="showDisabledModules"
      :title="$t('moduleMgmt.disabledModuleList')"
      width="500px"
    >
      <div v-if="disabledModules.length > 0" class="disabled-modules-list">
        <div v-for="mod in disabledModules" :key="mod.name" class="disabled-module-item">
          <span class="module-name">{{ mod.displayName || mod.name }}</span>
          <el-button
            type="primary"
            size="small"
            @click="toggleModule(mod)"
          >
            {{ $t('moduleMgmt.enable') }}
          </el-button>
        </div>
      </div>
      <div v-else class="empty-state">
        <p>{{ $t('moduleMgmt.noDisabledModules') }}</p>
      </div>
      <template #footer>
        <el-button @click="showDisabledModules = false">{{ $t('moduleMgmt.close') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { useRouter } from 'vue-router'
import {
  Refresh,
  InfoFilled,
  Folder,
  Connection,
  Odometer,
  RefreshRight,
  Setting,
  Cloudy,
  ArrowRight,
  Server,
  FolderOpened
} from '@element-plus/icons-vue'
import { API_BASE_URL } from "@/config"
import eventBus from '@/utils/eventBus'

export default {
  name: 'ModuleMgmt',

  components: {
    Refresh,
    InfoFilled,
    ArrowRight
  },

  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const modules = ref([])
    const moduleDetails = ref({})  // 存储模块详情
    const loading = ref(false)
    const operatingModule = ref('')

    // 详情对话框
    const infoDialogVisible = ref(false)
    const currentModuleInfo = ref({})
    const infoLoading = ref(false)

    // 禁用模块列表
    const showDisabledModules = ref(false)

    // 模块静态配置（顺序与导航栏一致）
    const moduleList = [
      { name: 'filemgmt', category: 'file', icon: 'FolderOpened', i18nKey: 'nav.fileManagement' },
      { name: 'fileSync', category: 'file', icon: 'RefreshRight', i18nKey: 'nav.fileSync' },
      { name: 'serverManagement', category: 'server', icon: 'Server', i18nKey: 'nav.serverManagement' },
      { name: 'mapping', category: 'server', icon: 'Setting', i18nKey: 'nav.serverMapping' },
      { name: 'p2p', category: 'p2p', icon: 'Odometer', i18nKey: 'nav.p2pManagement' },
      { name: 'proxy', category: 'proxy', icon: 'Connection', i18nKey: 'nav.proxyManagement' },
      { name: 'dns', category: 'dns', icon: 'Cloudy', i18nKey: 'nav.ddnsManagement' },
      { name: 'acme', category: 'dns', icon: 'Setting', i18nKey: 'nav.acmeManagement' },
      { name: 'mountManagement', category: 'file', icon: 'Folder', i18nKey: 'nav.mountSettings' },
      { name: 'deviceCenter', category: 'device', icon: 'Setting', i18nKey: 'nav.deviceCenter' },
    ]

    const moduleIcons = {
      FolderOpened,
      RefreshRight,
      Server,
      Setting,
      Odometer,
      Connection,
      Cloudy,
      Folder
    }

    const enabledCount = computed(() => modules.value.filter(m => m.enabled).length)
    const disabledCount = computed(() => modules.value.filter(m => !m.enabled).length)
    const disabledModules = computed(() => modules.value.filter(m => !m.enabled))

    const getModuleIcon = (name) => {
      const mod = moduleList.find(m => m.name === name)
      return mod ? (moduleIcons[mod.icon] || Setting) : Setting
    }
    const getModuleDisplayName = (name) => {
      const mod = moduleList.find(m => m.name === name)
      return mod ? t(mod.i18nKey) : name
    }
    const getModuleCategory = (category) => {
      const map = {
        file: t('moduleMgmt.categoryFile'),
        server: t('moduleMgmt.categoryServer'),
        p2p: t('moduleMgmt.categoryP2P'),
        proxy: t('moduleMgmt.categoryProxy'),
        dns: t('moduleMgmt.categoryDNS'),
        device: t('moduleMgmt.categoryDevice'),
      }
      return map[category] || category
    }
    const getModuleDeps = (name) => moduleDetails.value[name]?.depends || []
    const getModuleByName = (name) => modules.value.find(m => m.name === name)

    const getStatusClass = (mod) => {
      if (mod.error) return 'error'
      return mod.enabled ? 'running' : 'stopped'
    }

    const getStatusText = (mod) => {
      if (mod.error) return t('moduleMgmt.error')
      return mod.enabled ? t('moduleMgmt.running') : t('moduleMgmt.notEnabled')
    }

    const checkLogin = (data) => {
      if (data?.errCode === 1000) {
        ElMessage.warning(t('common.notLoggedIn'))
        router.push('/user-login?callback=module-mgmt')
        return false
      }
      if (data?.errCode === 1001) {
        ElMessage.warning(t('common.noPermission'))
        router.push('/')
        return false
      }
      return true
    }

    const fetchModules = async () => {
      try {
        loading.value = true
        const response = await fetch(`${API_BASE_URL}/modules`, {
          credentials: 'include'
        })
        const result = await response.json()
        if (!checkLogin(result)) return
        if (result.code === 0) {
          modules.value = result.data || []
        } else {
          ElMessage.error(result.msg || t('common.requestFailed'))
        }
      } catch (error) {
        console.error(t('common.requestFailed'), error)
        ElMessage.error(t('common.requestFailed'))
      } finally {
        loading.value = false
      }
    }

    const fetchModuleInfo = async (name) => {
      try {
        infoLoading.value = true
        const response = await fetch(`${API_BASE_URL}/modules/${name}/info`, {
          credentials: 'include'
        })
        const result = await response.json()
        if (!checkLogin(result)) return
        if (result.code === 0) {
          currentModuleInfo.value = result.data || {}
          // 保存模块详情
          moduleDetails.value[name] = result.data
        } else {
          ElMessage.error(result.msg || '获取模块详情失败')
        }
      } catch (error) {
        console.error('fetchModuleInfo error:', error)
        ElMessage.error('获取模块详情失败')
      } finally {
        infoLoading.value = false
      }
    }

    const showModuleInfo = async (name) => {
      currentModuleInfo.value = { name }
      infoDialogVisible.value = true
      await fetchModuleInfo(name)
    }

    const toggleModule = async (mod) => {
      const action = mod.enabled ? 'disable' : 'enable'
      const displayName = mod.displayName || mod.name
      try {
        operatingModule.value = mod.name
        const response = await fetch(`${API_BASE_URL}/modules/${mod.name}/${action}`, {
          method: 'POST',
          credentials: 'include'
        })
        const result = await response.json()
        if (!checkLogin(result)) return
        if (result.code === 0) {
          ElMessage.success(`${displayName} ${action === 'enable' ? t('moduleMgmt.enable') : t('moduleMgmt.disable')}${t('common.operationSuccess')}`)
          await fetchModules()
          // 通知导航栏刷新模块状态
          eventBus.emit('module-changed', { name: mod.name, enabled: !mod.enabled })
        } else {
          ElMessage.error(result.msg || t('common.operationFailed'))
        }
      } catch (error) {
        console.error('toggleModule error:', error)
        ElMessage.error(t('common.operationFailed'))
      } finally {
        operatingModule.value = ''
      }
    }

    const refreshModules = () => {
      fetchModules()
    }

    onMounted(() => {
      fetchModules()
    })

    return {
      modules,
      loading,
      operatingModule,
      enabledCount,
      disabledCount,
      disabledModules,
      infoDialogVisible,
      currentModuleInfo,
      infoLoading,
      showDisabledModules,
      getModuleIcon,
      getModuleDisplayName,
      getModuleCategory,
      getModuleDeps,
      getModuleByName,
      getStatusClass,
      getStatusText,
      fetchModules,
      fetchModuleInfo,
      showModuleInfo,
      toggleModule,
      refreshModules
    }
  }
}
</script>

<style scoped>
.module-mgmt-container {
  padding: 20px;
}

.stats-row {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: #fff;
  border-radius: 8px;
  padding: 16px 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 100px;
}

.stat-card.running {
  background: linear-gradient(135deg, #67c23a 0%, #85ce61 100%);
  color: #fff;
}

.stat-card.enabled {
  background: linear-gradient(135deg, #e6a23c 0%, #ebb563 100%);
  color: #fff;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
}

.stat-label {
  font-size: 14px;
  margin-top: 4px;
  opacity: 0.9;
}

.module-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.card-title-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.card-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.info-tag {
  padding: 2px 8px;
  background: #ecf5ff;
  color: #409eff;
  border-radius: 4px;
  font-size: 12px;
}

.card-actions {
  display: flex;
  gap: 8px;
}

.card-body {
  padding: 20px;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
}

.empty-state .el-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.module-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 16px;
}

.module-item {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.3s;
}

.module-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.module-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.module-icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #409eff;
  color: #fff;
  border-radius: 8px;
  font-size: 20px;
}

.module-icon.webdav { background: #67c23a; }
.module-icon.proxy { background: #e6a23c; }
.module-icon.p2p { background: #f56c6c; }
.module-icon.dns { background: #9c27b0; }
.module-icon.fileSync { background: #00bcd4; }
.module-icon.mapping { background: #3f51b5; }

.module-info {
  flex: 1;
}

.module-name {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.module-category {
  font-size: 12px;
  color: #999;
}

.module-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.module-body {
  margin-bottom: 12px;
  padding: 12px;
  background: #fff;
  border-radius: 4px;
}

.status-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.status-row:last-child {
  margin-bottom: 0;
}

.status-row .label {
  color: #666;
  font-size: 14px;
}

.status-row .value {
  color: #333;
  font-size: 14px;
}

.status-row .value.running {
  color: #67c23a;
  font-weight: 500;
}

.status-row .value.stopped {
  color: #909399;
}

.status-row .value.error {
  color: #f56c6c;
}

.error-text {
  word-break: break-all;
}

.deps-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  flex-wrap: wrap;
}

.deps-label {
  color: #909399;
  font-size: 12px;
}

.dep-tag {
  margin-right: 4px;
}

.module-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.startup-order-list {
  max-height: 400px;
  overflow-y: auto;
}

.order-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border-bottom: 1px solid #eee;
}

.order-item:last-child {
  border-bottom: none;
}

.order-num {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #409eff;
  color: #fff;
  border-radius: 50%;
  font-size: 12px;
  font-weight: bold;
}

.order-name {
  flex: 1;
  font-size: 14px;
}

.disabled-modules-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.disabled-module-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 8px;
}

.disabled-module-item .module-name {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}
</style>
